/**
 * Folds text for matching and records, per folded character, the index it came from. Meilisearch
 * folds accents itself, so a hit on "Dvorak" has to be markable inside "Dvořák" — and the map is
 * what lets the mark land on the original string.
 */
function fold(text: string): { folded: string; origin: Array<number> } {
  const folded: Array<string> = [];
  const origin: Array<number> = [];

  for (let index = 0; index < text.length; index++) {
    for (const char of text[index]
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()) {
      folded.push(char);
      origin.push(index);
    }
  }

  return { folded: folded.join(''), origin };
}

/**
 * `[start, end)` slices of `text` that the query matches, merged and in order.
 *
 * Matched per whitespace-separated token, because Meilisearch matches words independently: the
 * words of "marathon freiburg" can appear in either order, or apart. A typo-tolerant hit simply
 * yields no range — the row still shows, just without a mark.
 */
export function highlightRanges(text: string, query: string): Array<[number, number]> {
  const tokens = fold(query)
    .folded.split(/\s+/)
    .filter((token) => token !== '');
  if (tokens.length === 0) return [];

  const { folded, origin } = fold(text);
  const ranges: Array<[number, number]> = [];

  for (const token of tokens) {
    let from = 0;
    let at = folded.indexOf(token, from);
    while (at >= 0) {
      ranges.push([origin[at], origin[at + token.length - 1] + 1]);
      from = at + token.length;
      at = folded.indexOf(token, from);
    }
  }

  ranges.sort((a, b) => a[0] - b[0]);

  return ranges.reduce<Array<[number, number]>>((merged, range) => {
    const last = merged.at(-1);
    if (last != null && range[0] <= last[1]) {
      last[1] = Math.max(last[1], range[1]);
      return merged;
    }
    merged.push(range);
    return merged;
  }, []);
}
